import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import { Button } from "../../../components/ui/common/button";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import LoadingOverlay from "../ingredientList/components/LoadingOverlay";
import { IngredientRowViewModel } from "../ingredientList/types/ingredientListView.types";
import CalculateConfirmDialog from "../ingredientRegister/components/CalculateConfirmDialog";
import EnvironmentalDataCard from "../ingredientRegister/components/EnvironmentalDataCard";
import IngredientRegisterFooter from "../ingredientRegister/components/IngredientRegisterFooter";
import RegisterDataSections from "../ingredientRegister/components/RegisterDataSections";
import RegisterFormProvider from "../ingredientRegister/components/RegisterFormProvider";
import UnitDetailsCard from "../ingredientRegister/components/UnitDetailsCard";
import IngredientEditInfoCard from "./components/IngredientEditInfoCard";
import { useIngredientEditViewModel } from "./hooks/useIngredientEditViewModel";

interface IngredientEditModalProps {
  ingredientIdToUpdate: IngredientRowViewModel | undefined;
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  refetchIngredients: () => void;
  isEnableEnvironmentData?: boolean;
  isEnableIngredientMapping?: boolean;
}

/**
 * Tailwind rebuild of the legacy `IngredientEditModal`
 * (src/screens/ingredients/ingredientEdit/IngredientEditModal.tsx) — same
 * layout/behavior as the Tailwind register modal (`IngredientRegisterModal.tsx`),
 * reusing its Unit Details / Environmental Data / Packaging / Transportation
 * / LCA cards and footer directly (all driven by the same
 * `IngredientRegisterViewModel` shape — see `useIngredientEditViewModel.ts`).
 * Only Ingredient Info differs (`IngredientEditInfoCard`): Ingredient Name
 * and Supplier Name are read-only here, matching the legacy edit flow, which
 * never lets you re-pick either. `isEnableIngredientMapping` is accepted for
 * drop-in prop compatibility but — matching the legacy component exactly —
 * isn't read anywhere.
 */
const IngredientEditModal = ({
  ingredientIdToUpdate,
  visibleModal,
  setVisibleModal,
  refetchIngredients,
  isEnableEnvironmentData = false
}: IngredientEditModalProps) => {
  const vm = useIngredientEditViewModel({
    ingredientIdToUpdate,
    visibleModal,
    setVisibleModal,
    refetchIngredients,
    isEnableEnvironmentData
  });

  return (
    <>
      <TailwindSideModal
        open={visibleModal}
        onOpenChange={open => {
          if (!open) vm.onCancel();
        }}
        title="Edit Ingredient"
        onOpenAutoFocus={e => e.preventDefault()}
        contentClassName="tw-bg-[#FBFAF7]"
      >
        <CommonSplitContainer
          currentHeight={0.92}
          overflowX="hidden"
          topChild={
            <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-3">
              <IngredientEditInfoCard vm={vm} />

              <RegisterFormProvider key={vm.formSyncKey} control={vm.control} errors={vm.errors}>
                <UnitDetailsCard vm={vm} />
                <EnvironmentalDataCard vm={vm} />
              </RegisterFormProvider>

              <RegisterDataSections vm={vm} />
            </div>
          }
          bottomChild={
            <>
              {vm.isIngredientMappingMode && vm.enableIngredientMapping ? (
                <div className="tw-flex tw-justify-end tw-border-t tw-border-border tw-px-6 tw-pt-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={vm.enableIngredientButton}
                    onClick={vm.onNavigateIngredientMapping}
                  >
                    Ingredient Mapping
                  </Button>
                </div>
              ) : null}
              <IngredientRegisterFooter vm={vm} />
            </>
          }
        />
      </TailwindSideModal>

      <CalculateConfirmDialog vm={vm} />

      {vm.isLoadingPutDataToQueue ? <LoadingOverlay /> : null}
    </>
  );
};

export default IngredientEditModal;
